import { stringify } from "qs";
import { useEffect } from "react";
import {
    FILTER_ALERT,
    type FilterAlertTypeEnum,
} from "src/components/Filters/FilterAlert/hooks/useFilterAlert.hooks";
import { useQueryString } from "src/design-system/core/hooks";
import { useRouteMetadata } from "src/hooks";
import { PATH_AGENDA } from "src/routesConstants";
import type { Id } from "src/types/id";
import { OfferStatus } from "src/types/offerSlot";
import { PageEnum } from "src/utils/tracking/types/pageEnum";

export const AGENDA_LIST_QS_KEY = "agenda-tab";
export const AGENDA_BINDER_STATUS_QS_KEY = "agenda-binder-status";
export const AGENDA_LIST_SLOT_ID_QS_KEY = "slot-ids";

export enum AgendaListTabsEnum {
    OFFER = "offers",
    SPONSORED_AD = "sponsored-ads",
}

const agendaTabToPage: Record<AgendaListTabsEnum, PageEnum> = {
    [AgendaListTabsEnum.OFFER]: PageEnum.OfferSchedule,
    [AgendaListTabsEnum.SPONSORED_AD]: PageEnum.SponsoredAdSchedule,
};

export const agendaRoutes = {
    tablesView: ({
        tab,
        slotIds,
        filterAlert,
        binderStatus,
    }: {
        tab?: AgendaListTabsEnum;
        slotIds?: number[];
        filterAlert?: FilterAlertTypeEnum;
        binderStatus?: OfferStatus;
    } = {}): string =>
        `${PATH_AGENDA}${stringify(
            {
                [AGENDA_LIST_QS_KEY]: tab,
                [AGENDA_LIST_SLOT_ID_QS_KEY]: slotIds,
                [FILTER_ALERT]: filterAlert,
                [AGENDA_BINDER_STATUS_QS_KEY]: binderStatus,
            },
            { addQueryPrefix: true }
        )}`,
};

export function useAgendaListTabs(): {
    agendaListTab: AgendaListTabsEnum;
    setAgendaListTab: (agendaListTab: AgendaListTabsEnum) => void;
} {
    const [agendaListTab, setAgendaListTab] = useQueryString(
        AGENDA_LIST_QS_KEY,
        AgendaListTabsEnum.OFFER
    );
    const { updateMetadata } = useRouteMetadata();

    useEffect(() => {
        const resolvedTab = agendaListTab ?? AgendaListTabsEnum.OFFER;
        updateMetadata({
            page: agendaTabToPage[resolvedTab] ?? PageEnum.OfferSchedule,
        });
    }, [agendaListTab, updateMetadata]);

    return { agendaListTab: agendaListTab ?? AgendaListTabsEnum.OFFER, setAgendaListTab };
}

export function useAgendaBinderStatusTabs(defaultValue?: OfferStatus | number): {
    agendaBinderStatusTab: OfferStatus | undefined;
    setAgendaBinderStatusTab: (agendaBinderStatusTab: OfferStatus | undefined) => void;
} {
    const [agendaBinderStatusTab, setAgendaBinderStatusTab] = useQueryString<
        string,
        OfferStatus | undefined
    >(AGENDA_BINDER_STATUS_QS_KEY, (defaultValue as OfferStatus) ?? OfferStatus.Ongoing);

    return {
        agendaBinderStatusTab: agendaBinderStatusTab,
        setAgendaBinderStatusTab,
    };
}

export const AGENDA_MODAL_QS_KEY = "agenda-modals";
export enum AgendaModalEnum {
    suspendSpecial = "suspendSpecial",
    suspendDuo = "suspendDuo",
    reactivateDuo = "reactivateDuo",
    suspendAutoRule = "suspendAutoRule",
    reactivateAutoRule = "reactivateAutoRule",
    visualizeSmart = "visualizeSmart",
}
export function useAgendaModals(): {
    agendaModal: AgendaModalEnum;
    setAgendaModal: (agendaModal: AgendaModalEnum) => void;
} {
    const [agendaModal, setAgendaModal] = useQueryString(AGENDA_MODAL_QS_KEY, undefined);

    return { agendaModal, setAgendaModal };
}

export function useAgendaOnlySlotId(): {
    onlySlotId?: Id;
    setOnlySlotId: (onlySlotId: Id) => void;
} {
    const [onlySlotId, setOnlySlotId] = useQueryString(AGENDA_LIST_SLOT_ID_QS_KEY);

    return {
        onlySlotId: onlySlotId ? parseInt(onlySlotId.toString(), 10) : undefined,
        setOnlySlotId: (onlySlotId: Id) => setOnlySlotId(onlySlotId?.toString()),
    };
}
